import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import {
  FULL_CAROUSEL_BREAKPOINT_PROPS,
  imageSizes,
} from '@glass/web/components/Carousel/responsive';
import { CAROUSEL_SECTION_ID } from '@glass/web/modules/content/pages/contentIds';
import useIsDocumentReadyPriorityLoadedIdleInView from '@glass/web/modules/loading/useIsDocumentReadyPriorityLoadedIdleInView';
import { TEMPLATE_QUERY, TEMPLATE_VIEW_QUERY } from '@glass/web/modules/pages/queryParams';

import ResumeCarousel from '@glass/shared/components/ResumeCarousel/ResumeCarousel';
import Section from '@glass/shared/components/Section/Section';
import mergeRefs from '@glass/shared/helpers/utils/mergeRefs';
import withTrackingContext from '@glass/shared/modules/tracking/withTrackingContext';

const ctaQueryParams = { [TEMPLATE_VIEW_QUERY]: TEMPLATE_QUERY };
const CarouselSection = React.forwardRef(
  (
    {
      jSlug,
      iSlug,
      fallbackId,
      seedOverride,
      contentResumeSlug,
      children,
      title,
      subtitle,
      isAlternatingColor,
      type,
    },
    refProp,
  ) => {
    const { refInView, isDocumentReadyPriorityLoadedIdleInView } =
      useIsDocumentReadyPriorityLoadedIdleInView();

    const ref = useMemo(() => mergeRefs(refProp, refInView), [refProp, refInView]);

    return (
      <Section
        ref={ref}
        ctaQueryParams={ctaQueryParams}
        isAlternatingColor={isAlternatingColor}
        subtitle={subtitle}
        title={title}
        type={type}
      >
        {children}
        <ResumeCarousel
          autoPlay
          hasButtons
          hasLabels
          hasSubheader
          breakpointProps={FULL_CAROUSEL_BREAKPOINT_PROPS}
          contentResumeSlug={contentResumeSlug}
          fallbackId={fallbackId}
          iSlug={iSlug}
          imageSizes={imageSizes}
          jSlug={jSlug}
          origin="carousel-section"
          seedOverride={seedOverride}
          slidesToLoad={!isDocumentReadyPriorityLoadedIdleInView ? 0 : undefined}
        />
      </Section>
    );
  },
);

CarouselSection.defaultProps = {
  title: '$(resumeWords.capitalized) <b>templates</b> recruiters love',
  subtitle: `Choose one of these templates or $(verbWords.singular) your own using $(brand.title)'s advanced $(resumeWords.abbreviated) template editor`,
};

CarouselSection.propTypes = {
  children: PropTypes.node,
  contentResumeSlug: PropTypes.string,
  fallbackId: PropTypes.string,
  isAlternatingColor: PropTypes.bool,
  iSlug: PropTypes.string,
  jSlug: PropTypes.string,
  seedOverride: PropTypes.string,
  subtitle: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  type: PropTypes.string,
};

export default React.memo(withTrackingContext(CarouselSection, CAROUSEL_SECTION_ID));
